import React, { useState } from "react";
import {
  BookOpen,
  Clock,
  ChevronLeft,
  ArrowRight,
  ChevronUp,
} from "lucide-react";
import { chapters, personas, type Chapter } from "./journeyData";
import Tabs from "../common/Tabs";
import { useTranslation } from "react-i18next";

interface ReadingJourneyProps {
  compact?: boolean;
}

const getChapter = (id: number): Chapter | undefined =>
  chapters.find((ch) => ch.id === id);

const ReadingJourney: React.FC<ReadingJourneyProps> = ({ compact = false }) => {
  const { t } = useTranslation("home");
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<string>("engineer");
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [previewChapterId, setPreviewChapterId] = useState<number | null>(null);

  const persona = personas.find((p) => p.id === selectedPersona) || personas[0];
  const pathChapters = persona.corePath
    .map((id) => getChapter(id))
    .filter(Boolean) as Chapter[];

  const handlePersonaChange = (personaId: string) => {
    setSelectedPersona(personaId);
    setActiveStepIndex(0);
    setPreviewChapterId(null);
  };

  const activeChapter = previewChapterId
    ? getChapter(previewChapterId)
    : pathChapters[activeStepIndex] || pathChapters[0];

  if (!activeChapter) return null;

  const isFirst = activeStepIndex === 0;
  const isLast = activeStepIndex === pathChapters.length - 1;

  const handleStepChange = (index: number) => {
    setActiveStepIndex(index);
    setPreviewChapterId(null);
  };

  const handleNext = () => {
    setPreviewChapterId(null);
    if (!isLast) setActiveStepIndex((prev) => prev + 1);
  };

  const handlePrev = () => {
    setPreviewChapterId(null);
    if (!isFirst) setActiveStepIndex((prev) => prev - 1);
  };

  const handleExpandClick = (personaId?: string) => {
    if (personaId) {
      setSelectedPersona(personaId);
      setActiveStepIndex(0);
      setPreviewChapterId(null);
    }
    setIsExpanded(true);
  };

  // Compact mode: show inline prompt with persona buttons
  if (compact && !isExpanded) {
    return (
      <section className="my-8 w-full max-w-[1400px] mx-auto ">
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl p-6 border border-indigo-100 dark:border-indigo-800/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-slate-700 dark:text-slate-300 text-base">
                <span className="font-medium">{t("readingJourney.shortOnTime")}</span> {t("readingJourney.personalizedPath")}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {personas.map((p) => (
                <button
                  key={p.id}
                  onClick={() => handleExpandClick(p.id)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <p.icon className="w-4 h-4" />
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="my-16 w-full max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        {compact && (
          <button
            onClick={() => setIsExpanded(false)}
            className="inline-flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 mb-4 transition-colors"
          >
            <ChevronUp className="w-4 h-4" />
            {t("readingJourney.collapse")}
          </button>
        )}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-semibold uppercase tracking-wide mb-4">
          {t("readingJourney.badge")}
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
          {t("readingJourney.title")}
        </h2>
        <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
          {t("readingJourney.description")}
        </p>
      </div>

      {/* Role Selection Tabs */}
      <Tabs
        items={personas}
        selectedId={selectedPersona}
        onChange={handlePersonaChange}
        className="mb-12"
      />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Persona Info (Sticky) */}
        <div className="lg:col-span-4 lg:sticky lg:top-8 space-y-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-indigo-600 dark:text-indigo-400">
                <persona.icon className="w-8 h-8" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-slate-900 dark:text-white">
                  {persona.label} {t("readingJourney.path")}
                </h3>
                <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mt-1">
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-3.5 h-3.5" />
                    {persona.corePath.length} {t("readingJourney.chapters")}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {t("readingJourney.readTime", { hours: Math.round(persona.corePath.length * 0.75) })}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed mb-6">
              {persona.description}
            </p>

            <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
              <h4 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3">
                {t("readingJourney.whatYouWillMaster")}
              </h4>
              <ul className="space-y-2">
                {[
                  t("readingJourney.masterItems.coreConcepts"),
                  t("readingJourney.masterItems.strategicPatterns"),
                  t("readingJourney.masterItems.realWorldApplication"),
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400"
                  >
                    <div className="mt-1 w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-2.5 h-2.5 text-green-600 dark:text-green-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Right Column: Journey Carousel */}
        <div className="lg:col-span-8">
          {/* Micro Progress Bar */}
          <div className="flex gap-1.5 mb-8">
            {pathChapters.map((_, index) => (
              <button
                key={index}
                onClick={() => handleStepChange(index)}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  index <= activeStepIndex && !previewChapterId
                    ? "bg-indigo-600 dark:bg-indigo-500"
                    : "bg-slate-200 dark:bg-slate-700 hover:bg-indigo-200 dark:hover:bg-indigo-900"
                }`}
                aria-label={t("readingJourney.goToStep", { step: index + 1 })}
              />
            ))}
          </div>

          {/* Active Chapter Content (No Border/BG) */}
          <div className="transition-all duration-300">
            <div className="py-2">
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={`px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${
                    previewChapterId
                      ? "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400"
                      : "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300"
                  }`}
                >
                  {previewChapterId
                    ? t("readingJourney.deepDive")
                    : t("readingJourney.chapter", { id: activeChapter.id })}
                </span>
                {activeStepIndex === 0 && !previewChapterId && (
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                    <Sparkles className="w-3 h-3" /> {t("readingJourney.startHere")}
                  </span>
                )}
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4 leading-tight">
                {activeChapter.title}
              </h3>

              <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-8">
                {activeChapter.description}
              </p>

              <div className="flex items-center justify-between pt-6 border-t border-slate-100 dark:border-slate-800">
                {previewChapterId ? (
                  <button
                    onClick={() => setPreviewChapterId(null)}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                  >
                    {t("readingJourney.returnToPath")}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handlePrev}
                      disabled={isFirst}
                      className={`flex items-center gap-2 text-sm font-medium transition-colors
                    ${
                      isFirst
                        ? "text-slate-300 dark:text-slate-700 cursor-not-allowed"
                        : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                    }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t("readingJourney.previous")}
                    </button>

                    <button
                      onClick={handleNext}
                      disabled={isLast}
                      className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all
                    ${
                      isLast
                        ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white hover:-translate-y-0.5"
                    }`}
                    >
                      {isLast
                        ? t("readingJourney.pathCompleted")
                        : `${t("readingJourney.next")} (${activeStepIndex + 1}/${
                            pathChapters.length
                          })`}
                      {!isLast && <ArrowRight className="w-4 h-4" />}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Optional Deep Dives Hint */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-3 uppercase tracking-wider font-medium">
              {t("readingJourney.moreToExplore")}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {persona.optionalChapters.map((id) => (
                <button
                  key={id}
                  onClick={() => setPreviewChapterId(id)}
                  className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-all duration-200
                    ${
                      previewChapterId === id
                        ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 ring-2 ring-amber-500/20"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:text-indigo-600 dark:hover:text-indigo-400 hover:scale-110"
                    }`}
                  title={t("readingJourney.viewChapter", { id })}
                >
                  {id}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Helper component for Sparkles icon since it wasn't imported
const Sparkles = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
  </svg>
);

export default ReadingJourney;
